import React from 'react';
import { useTheme } from '../../theme/ThemeProvider';

const TemplateGallery = ({ templates, onUseTemplate, loading }) => {
  const { mode } = useTheme();
  
  const getTemplateIcon = (name) => {
    if (name.includes('Pivot')) return '📊';
    if (name.includes('Financiero') || name.includes('Financial')) return '💰';
    if (name.includes('Dashboard')) return '📈';
    if (name.includes('Tabla')) return '📋';
    return '🎨';
  };
  
  const styles = {
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
      gap: 'var(--spacing-xl)',
      marginTop: 'var(--spacing-lg)'
    },
    card: {
      backgroundColor: mode === 'dark' ? 'var(--dark-bg-secondary)' : 'var(--color-white)',
      borderRadius: 'var(--border-radius-lg)',
      border: `2px solid ${mode === 'dark' ? 'var(--dark-border-primary)' : 'var(--color-light-gray)'}`,
      overflow: 'hidden',
      transition: 'all 0.3s ease',
      cursor: 'pointer'
    },
    cardHeader: {
      padding: 'var(--spacing-xl)',
      background: mode === 'dark' 
        ? 'linear-gradient(135deg, rgba(41, 181, 232, 0.15) 0%, rgba(56, 193, 114, 0.15) 100%)'
        : 'linear-gradient(135deg, rgba(41, 181, 232, 0.08) 0%, rgba(56, 193, 114, 0.08) 100%)',
      borderBottom: `1px solid ${mode === 'dark' ? 'var(--dark-border-primary)' : 'var(--color-light-gray)'}`
    },
    icon: {
      fontSize: '48px',
      marginBottom: 'var(--spacing-sm)',
      textAlign: 'center'
    },
    title: {
      fontSize: 'var(--font-size-xl)',
      fontWeight: 'var(--font-weight-bold)',
      color: mode === 'dark' ? 'var(--dark-text-primary)' : 'var(--color-black)',
      marginBottom: 'var(--spacing-xs)',
      textAlign: 'center'
    },
    templateBadge: {
      display: 'inline-block',
      padding: 'var(--spacing-xs) var(--spacing-sm)',
      backgroundColor: mode === 'dark' ? 'rgba(139, 92, 246, 0.2)' : 'rgba(139, 92, 246, 0.1)',
      color: '#8B5CF6',
      borderRadius: 'var(--border-radius-full)',
      fontSize: 'var(--font-size-xs)',
      fontWeight: 'var(--font-weight-bold)',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    cardBody: {
      padding: 'var(--spacing-lg)'
    },
    description: {
      fontSize: 'var(--font-size-base)',
      color: mode === 'dark' ? 'var(--dark-text-secondary)' : 'var(--color-medium-gray)',
      lineHeight: 1.6,
      marginBottom: 'var(--spacing-lg)',
      minHeight: '60px'
    },
    stats: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr 1fr',
      gap: 'var(--spacing-sm)',
      marginBottom: 'var(--spacing-lg)'
    },
    statItem: {
      textAlign: 'center',
      padding: 'var(--spacing-sm)',
      backgroundColor: mode === 'dark' ? 'var(--dark-bg-tertiary)' : 'var(--color-light-gray)',
      borderRadius: 'var(--border-radius-md)'
    },
    statValue: {
      fontSize: 'var(--font-size-xl)',
      fontWeight: 'var(--font-weight-bold)',
      color: '#29B5E8',
      marginBottom: 'var(--spacing-xs)'
    },
    statLabel: {
      fontSize: 'var(--font-size-xs)',
      color: mode === 'dark' ? 'var(--dark-text-tertiary)' : 'var(--color-medium-gray)',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    actions: {
      display: 'flex',
      gap: 'var(--spacing-sm)'
    },
    useButton: {
      flex: 1,
      padding: 'var(--spacing-md)',
      borderRadius: 'var(--border-radius-md)',
      border: 'none',
      backgroundColor: '#29B5E8',
      color: '#FFFFFF',
      fontSize: 'var(--font-size-base)',
      fontWeight: 'var(--font-weight-semibold)',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 'var(--spacing-sm)'
    },
    previewButton: {
      padding: 'var(--spacing-md)',
      borderRadius: 'var(--border-radius-md)',
      border: `1px solid ${mode === 'dark' ? 'var(--dark-border-primary)' : 'var(--color-light-gray)'}`,
      backgroundColor: mode === 'dark' ? 'var(--dark-bg-tertiary)' : 'var(--color-white)',
      color: mode === 'dark' ? 'var(--dark-text-primary)' : 'var(--color-black)',
      fontSize: 'var(--font-size-base)',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    },
    emptyState: {
      textAlign: 'center',
      padding: 'var(--spacing-3xl)',
      color: mode === 'dark' ? 'var(--dark-text-tertiary)' : 'var(--color-medium-gray)'
    },
    emptyStateIcon: {
      fontSize: '64px',
      marginBottom: 'var(--spacing-md)',
      opacity: 0.5
    }
  };
  
  if (!templates || templates.length === 0) {
    return (
      <div style={styles.emptyState}>
        <div style={styles.emptyStateIcon}>🎨</div>
        <h3>No hay plantillas disponibles</h3>
        <p>Los protocolos por defecto se cargarán próximamente.</p>
      </div>
    );
  }
  
  return (
    <div style={styles.grid}>
      {templates.map((template) => {
        const layoutElements = template.layout_config?.elements || [];
        const elementCount = layoutElements.length;
        
        // Contar tipos de elementos
        const chartCount = layoutElements.filter(el => el.type === 'chart').length;
        const tableCount = layoutElements.filter(el => el.type === 'table').length;
        const metricCount = layoutElements.filter(el => el.type === 'metric').length;
        
        return (
          <div
            key={template.id}
            style={styles.card}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-8px)';
              e.currentTarget.style.boxShadow = mode === 'dark' 
                ? '0 12px 24px rgba(0, 0, 0, 0.4)' 
                : '0 12px 24px rgba(0, 0, 0, 0.15)';
              e.currentTarget.style.borderColor = '#29B5E8';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.borderColor = mode === 'dark' ? 'var(--dark-border-primary)' : 'var(--color-light-gray)';
            }}
          >
            {/* Header */}
            <div style={styles.cardHeader}>
              <div style={styles.icon}>
                {getTemplateIcon(template.name)}
              </div>
              <h3 style={styles.title}>{template.name}</h3>
              <div style={{ textAlign: 'center', marginTop: 'var(--spacing-sm)' }}>
                <span style={styles.templateBadge}>⭐ Plantilla</span>
              </div>
            </div>
            
            {/* Body */}
            <div style={styles.cardBody}>
              <p style={styles.description}>
                {template.description || 'Plantilla de protocolo predefinida lista para usar.'}
              </p>
              
              {/* Estadísticas */}
              <div style={styles.stats}>
                <div style={styles.statItem}>
                  <div style={styles.statValue}>{elementCount}</div>
                  <div style={styles.statLabel}>Elementos</div>
                </div>
                <div style={styles.statItem}>
                  <div style={styles.statValue}>{chartCount + tableCount}</div>
                  <div style={styles.statLabel}>Visuales</div>
                </div>
                <div style={styles.statItem}>
                  <div style={styles.statValue}>{metricCount}</div>
                  <div style={styles.statLabel}>KPIs</div>
                </div>
              </div>
              
              {/* Acciones */}
              <div style={styles.actions}>
                <button
                  style={styles.useButton}
                  onClick={() => onUseTemplate(template.id)}
                  disabled={loading}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#1B96C7';
                    e.currentTarget.style.transform = 'scale(1.02)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#29B5E8';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  <span>🚀</span>
                  <span>Usar Plantilla</span>
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TemplateGallery;


